import type { WoodworkManifestProject } from '../content/woodworkManifest'

/** Normalized catalog from woodwork/manifest.json (index or legacy monolithic). */
export type WoodworkCatalog = {
  readonly version: number
  readonly updatedAt?: string
  readonly projectIds: readonly string[]
  /** Legacy manifest: full projects embedded in the root file */
  readonly inlinedProjects?: readonly WoodworkManifestProject[]
}

function isProjectObject(value: unknown): value is WoodworkManifestProject {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as WoodworkManifestProject).id === 'string' &&
    typeof (value as WoodworkManifestProject).title === 'string'
  )
}

export function parseWoodworkIndex(data: unknown): WoodworkCatalog {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Woodwork index is not a JSON object')
  }

  const record = data as { version?: number; updatedAt?: string; projects?: unknown }
  const version = record.version ?? 1
  const updatedAt = record.updatedAt
  const projects = record.projects

  if (!Array.isArray(projects)) {
    throw new Error('Woodwork index missing projects array')
  }

  if (projects.length === 0) {
    return { version, updatedAt, projectIds: [] }
  }

  if (typeof projects[0] === 'string') {
    return { version, updatedAt, projectIds: projects as string[] }
  }

  if (isProjectObject(projects[0])) {
    const inlined = projects as WoodworkManifestProject[]
    return {
      version,
      updatedAt,
      projectIds: inlined.map((p) => p.id),
      inlinedProjects: inlined
    }
  }

  throw new Error('Woodwork index projects must be ids (strings) or project objects')
}
